import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  ClipboardList,
  Sparkles,
  AlertTriangle,
  Lock,
  Edit,
} from 'lucide-react';

const OverviewTab = ({ exams, latestAnalysis, handleOpenExam, handleNewExam, setActiveTab }) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="border-[#E5E4E0] bg-white">
        <CardHeader>
          <CardTitle className="text-lg font-['Work_Sans'] flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-[#4B6651]" />
            Recent Exams
          </CardTitle>
        </CardHeader>
        <CardContent>
          {exams.length > 0 ? (
            <div className="space-y-3">
              {exams.slice(-3).map((exam) => {
                const isLocked = exam.is_locked;
                return (
                  <button
                    key={exam.id}
                    onClick={() => handleOpenExam(exam)}
                    className="w-full p-3 bg-[#F8F7F4] rounded-lg text-left hover:bg-[#E5E4E0] transition-colors"
                    data-testid={`exam-item-${exam.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-[#2A2C2A]">Exam #{exams.indexOf(exam) + 1}</p>
                      <div className="flex items-center gap-2">
                        {isLocked ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#5C5F5C]/10 text-[#5C5F5C] rounded text-xs">
                            <Lock className="w-3 h-3" />
                            Locked
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#4B6651]/10 text-[#4B6651] rounded text-xs">
                            <Edit className="w-3 h-3" />
                            Editable
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-[#5C5F5C]">
                      Mental Status: {exam.mental_status}
                    </p>
                    <p className="text-xs text-[#5C5F5C] mt-1">
                      {new Date(exam.created_at).toLocaleString()}
                    </p>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-[#5C5F5C] text-center py-8">
              No exams recorded yet. Start a neurological examination.
            </p>
          )}
          <Button
            onClick={handleNewExam}
            className="w-full mt-4 bg-[#4B6651] hover:bg-[#3E5543] text-white"
            data-testid="start-exam-btn"
          >
            {exams.length > 0 ? 'New Exam' : 'Start Exam'}
          </Button>
        </CardContent>
      </Card>

      <Card className="border-[#E5E4E0] bg-white">
        <CardHeader>
          <CardTitle className="text-lg font-['Work_Sans'] flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#C06144]" />
            Latest Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          {latestAnalysis ? (
            <div>
              {latestAnalysis.rule_based_localization?.abnormal_findings?.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs text-[#5C5F5C] uppercase tracking-wider font-semibold mb-2">
                    Abnormal Findings
                  </p>
                  <div className="space-y-1">
                    {latestAnalysis.rule_based_localization.abnormal_findings.map((finding, idx) => (
                      <div key={`finding-${finding.slice(0,20)}-${idx}`} className="flex items-start gap-2 text-sm">
                        <AlertTriangle className="w-4 h-4 text-[#C06144] mt-0.5 flex-shrink-0" />
                        <span className="text-[#2A2C2A]">{finding}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {latestAnalysis.rule_based_localization?.possible_locations?.length > 0 && (
                <div>
                  <p className="text-xs text-[#5C5F5C] uppercase tracking-wider font-semibold mb-2">
                    Possible Lesion Locations
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {latestAnalysis.rule_based_localization.possible_locations.map((loc) => (
                      <span key={`loc-${loc}`} className="px-2 py-1 bg-[#4B6651]/10 text-[#4B6651] rounded text-sm">
                        {loc}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <Button
                onClick={() => setActiveTab('analysis')}
                variant="outline"
                className="w-full mt-4 border-[#4B6651] text-[#4B6651] hover:bg-[#4B6651] hover:text-white"
              >
                View Full Analysis
              </Button>
            </div>
          ) : (
            <p className="text-[#5C5F5C] text-center py-8">
              Complete a neurological exam to generate AI analysis.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OverviewTab;
